import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * NABadge — render an "N/A" pill with an info icon and tooltip showing the reason.
 * Use everywhere a metric is unavailable instead of showing "0" / "0%" / "₹0.00".
 *
 * Props:
 *   reason: string — the na_reason to show in the tooltip
 *   className: optional Tailwind classes
 *   inline: when true renders just the badge inline (default size matches normal text)
 */
export const NABadge = ({ reason, className = '', inline = false, dataTestId }) => {
  const tip = reason || 'Data not available — not yet computed for this brand.';
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 cursor-help ${inline ? '' : 'px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30'} text-amber-400 ${className}`}
            data-testid={dataTestId || 'na-badge'}
          >
            <span className="font-semibold tabular-nums">N/A</span>
            <AlertCircle className="w-3.5 h-3.5 opacity-70" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm text-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * MetricValue — wraps a value display so that null/undefined/NaN automatically
 * render as an NABadge with the supplied reason. Use across all dashboard cards.
 *
 * Props:
 *   value: any
 *   reason: string — na_reason
 *   formatter: function(v) -> string — optional custom formatter
 *   prefix, suffix: optional decorations around the formatted value
 *   className: passed to outer span
 */
export const MetricValue = ({ value, reason, formatter, prefix = '', suffix = '', className = '', dataTestId }) => {
  const isNA = value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));
  if (isNA) return <NABadge reason={reason} dataTestId={dataTestId} />;
  const display = formatter ? formatter(value) : String(value);
  return (
    <span className={className} data-testid={dataTestId}>
      {prefix}{display}{suffix}
    </span>
  );
};

export default NABadge;
